import classNames from 'classnames';
import isEmpty from 'lodash-es/isEmpty';
import { ChangeEvent, FocusEvent, ForwardedRef, useEffect } from 'react';
import {
    defaultCountries,
    usePhoneInput,
    CountrySelector,
    DialCodePreview,
} from 'react-international-phone';
import { useIntl } from 'react-intl';

import useEmailPhoneAutocomplete from '../../hooks/useEmailPhoneAutocomplete';
import { mergeRefs } from '../../lib/utils';

import TextField, { TextFieldProps } from './TextField';

import styles from '../../styles/fields/contact-field.module.css';
import 'react-international-phone/style.css';

export interface ContactFieldProps extends Omit<
    TextFieldProps,
    'type' | 'onChange' | 'onFocus' | 'onBlur'
> {
    type?: string | null;
    defaultType?: string;
    value?: string | null;
    placeholder?: string | null;
    required?: boolean;
    withBorder?: boolean;
    withoutAutocomplete?: boolean;
    onTypeChange?: ((type: string) => void) | null;
    onChange?: ((value: string | null) => void) | null;
    onFocus?: ((e: FocusEvent<HTMLInputElement>) => void) | null;
    onBlur?: ((e: FocusEvent<HTMLInputElement>) => void) | null;
    className?: string | null;
    inputClassName?: string | null;
    [key: string]: unknown;
}

function ContactField({
    type = null,
    value: initialValue = null,
    defaultType = 'email',
    placeholder = null,
    required = false,
    withBorder = false,
    withoutAutocomplete = false,
    onTypeChange = null,
    onChange = null,
    onFocus = null,
    onBlur = null,
    className = null,
    inputClassName = null,
    ref: inputRef = null,
    ...props
}: ContactFieldProps & { ref?: ForwardedRef<HTMLInputElement> | null }) {
    const intl = useIntl();
    const onAutocompleteChange = (newValue: string | null, fieldType: string) => {
        if (fieldType !== 'tel' && onChange !== null) {
            const finalValue = newValue || null;
            onChange(finalValue);
        }
    };

    const {
        ref: autoCompleteRef,
        value,
        suggestion = null,
        placeholder: customPlaceholder,
        onChange: onInputChange,
        setValue,
        setType,
        type: fieldType = null,
    } = useEmailPhoneAutocomplete({
        value: initialValue,
        autocomplete: !withoutAutocomplete,
        onChange: onAutocompleteChange,
        onTypeChange,
        type,
        defaultType,
    });

    const onPhoneChange = (newValue: { inputValue: string; phone: string }) => {
        if (fieldType === 'tel' && onChange !== null) {
            onChange(newValue.inputValue.length > 0 ? newValue.phone : null);
        }
    };

    const {
        inputValue,
        handlePhoneValueChange,
        inputRef: phoneRef,
        country,
        setCountry,
    } = usePhoneInput({
        defaultCountry: 'ca',
        value: value || '',
        countries: defaultCountries,
        disableDialCodePrefill: true,
        disableDialCodeAndPrefix: true,
        onChange: onPhoneChange,
    });

    useEffect(() => {
        if (initialValue === null) {
            setValue(initialValue);
        }
    }, [initialValue]);

    let defaultPlaceholder = intl.formatMessage({
        defaultMessage: 'Entrez votre # de tel ou courriel',
        description: 'Field placeholder',
    });
    if (type === 'email') {
        defaultPlaceholder = intl.formatMessage({
            defaultMessage: 'Entrez votre courriel',
            description: 'Field placeholder',
        });
    } else if (type === 'tel') {
        defaultPlaceholder = intl.formatMessage({
            defaultMessage: 'Entrez votre # de téléphone',
            description: 'Field placeholder',
        });
    }

    const finalPlaceholder =
        (!withoutAutocomplete && !isEmpty(value) ? suggestion || customPlaceholder : placeholder) ||
        defaultPlaceholder;

    const onFieldChange = (newValue: string | null, e: ChangeEvent<HTMLInputElement>) => {
        onInputChange(newValue);
        if (fieldType === 'tel') {
            handlePhoneValueChange(e);
        }
    };

    return (
        <div className={classNames([styles.container, styles[fieldType], className])}>
            {fieldType === 'tel' ? (
                <div className={styles.phoneSelector}>
                    <CountrySelector
                        selectedCountry={country.iso2}
                        onSelect={({ iso2 }) => setCountry(iso2)}
                        className={styles.country}
                    />
                    <DialCodePreview
                        dialCode={country.dialCode}
                        prefix="+"
                        className={styles.dialCode}
                    />
                </div>
            ) : null}
            <TextField
                {...props}
                ref={mergeRefs(autoCompleteRef, fieldType === 'tel' ? phoneRef : null, inputRef)}
                type={fieldType}
                required={required}
                placeholder={finalPlaceholder}
                value={fieldType === 'tel' ? inputValue : value}
                autoComplete={fieldType}
                withBorder={withBorder}
                className={classNames([styles.input, inputClassName])}
                onFocus={onFocus}
                onBlur={onBlur}
                onChange={onFieldChange}
            />
        </div>
    );
}

export default ContactField;
